import React, { useMemo } from 'react';
import './NetworkVisualizations.css';

const IP_PATTERN = '(?:\\d{1,3}\\.){3}\\d{1,3}|[a-fA-F0-9:]{3,}';

const extractFlow = (finding) => {
  const text = [
    finding.title,
    finding.description,
    finding.code_snippet,
    finding.data_flow_trace,
  ].filter(Boolean).join(' ');

  const patterns = [
    new RegExp(`Src:\\s*(${IP_PATTERN}),\\s*Dst:\\s*(${IP_PATTERN})(?::(\\d+))?`, 'i'),
    new RegExp(`Source:\\s*(${IP_PATTERN}).*Destination:\\s*(${IP_PATTERN})(?::(\\d+))?`, 'i'),
    new RegExp(`between\\s+(${IP_PATTERN})\\s+and\\s+(${IP_PATTERN})(?::(\\d+))?`, 'i'),
    new RegExp(`(${IP_PATTERN})\\s*->\\s*(${IP_PATTERN})(?::(\\d+))?`, 'i'),
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      return {
        source: match[1],
        target: match[2],
        port: match[3],
        severity: finding.severity || 'low',
        type: finding.type || 'network',
      };
    }
  }

  const sourceOnly = text.match(new RegExp(`Source:\\s*(${IP_PATTERN})`, 'i'));
  if (sourceOnly) {
    return {
      source: sourceOnly[1],
      target: finding.type || 'network-event',
      severity: finding.severity || 'low',
      type: finding.type || 'network',
    };
  }

  return null;
};

const buildGraph = (findings) => {
  const edgeMap = new Map();
  const nodeMap = new Map();

  findings.forEach((finding) => {
    const flow = extractFlow(finding);
    if (!flow) return;

    const key = `${flow.source}->${flow.target}:${flow.port || ''}:${flow.type}`;
    const existing = edgeMap.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      edgeMap.set(key, { ...flow, count: 1 });
    }

    [flow.source, flow.target].forEach((id) => {
      const existingNode = nodeMap.get(id);
      const severity = flow.severity;
      if (!existingNode) {
        nodeMap.set(id, { id, severity, count: 1 });
      } else {
        existingNode.count += 1;
        if (severity === 'high' || (severity === 'medium' && existingNode.severity === 'low')) {
          existingNode.severity = severity;
        }
      }
    });
  });

  return {
    nodes: Array.from(nodeMap.values()),
    edges: Array.from(edgeMap.values()),
  };
};

const getNodePosition = (index, total) => {
  const centerX = 300;
  const centerY = 170;
  const radiusX = total <= 2 ? 140 : 230;
  const radiusY = total <= 2 ? 0 : 115;
  const angle = (2 * Math.PI * index) / Math.max(total, 1) - Math.PI / 2;

  if (total === 1) return { x: centerX, y: centerY };
  if (total === 2) return { x: index === 0 ? 170 : 430, y: centerY };

  return {
    x: centerX + radiusX * Math.cos(angle),
    y: centerY + radiusY * Math.sin(angle),
  };
};

const severityClass = (severity) => {
  if (severity === 'high') return 'high';
  if (severity === 'medium') return 'medium';
  return 'low';
};

function TrafficGraph({ findings }) {
  const graph = useMemo(() => buildGraph(findings), [findings]);
  const positionedNodes = useMemo(() => {
    const limitedNodes = graph.nodes.slice(0, 12);
    return limitedNodes.map((node, index) => ({
      ...node,
      ...getNodePosition(index, limitedNodes.length),
    }));
  }, [graph.nodes]);

  const nodeLookup = useMemo(() => {
    return new Map(positionedNodes.map((node) => [node.id, node]));
  }, [positionedNodes]);

  const visibleEdges = graph.edges.filter((edge) => nodeLookup.has(edge.source) && nodeLookup.has(edge.target));

  return (
    <div className="network-visual-card">
      <div className="network-section-header">
        <h2>Traffic Flow Graph</h2>
        <span>{visibleEdges.length} flows</span>
      </div>

      {positionedNodes.length === 0 ? (
        <p className="network-viz-empty">No source/destination flows were present in the detected findings.</p>
      ) : (
        <svg className="traffic-graph" viewBox="0 0 600 340" role="img" aria-label="Network traffic flow graph">
          <defs>
            <marker id="network-arrow" markerWidth="8" markerHeight="8" refX="7" refY="3" orient="auto" markerUnits="strokeWidth">
              <path d="M0,0 L0,6 L7,3 z" />
            </marker>
          </defs>
          {visibleEdges.map((edge, index) => {
            const source = nodeLookup.get(edge.source);
            const target = nodeLookup.get(edge.target);
            return (
              <g key={`${edge.source}-${edge.target}-${index}`}>
                <line
                  className={`traffic-edge ${severityClass(edge.severity)}`}
                  x1={source.x}
                  y1={source.y}
                  x2={target.x}
                  y2={target.y}
                  markerEnd="url(#network-arrow)"
                />
                {edge.port && (
                  <text
                    className="traffic-edge-label"
                    x={(source.x + target.x) / 2}
                    y={(source.y + target.y) / 2 - 6}
                  >
                    {edge.port}
                  </text>
                )}
              </g>
            );
          })}
          {positionedNodes.map((node) => (
            <g key={node.id} className="traffic-node">
              <circle className={`traffic-node-circle ${severityClass(node.severity)}`} cx={node.x} cy={node.y} r="24" />
              <text className="traffic-node-label" x={node.x} y={node.y + 44}>
                {node.id.length > 18 ? `${node.id.slice(0, 18)}...` : node.id}
              </text>
            </g>
          ))}
        </svg>
      )}
    </div>
  );
}

export default TrafficGraph;
